import moment from 'moment';
import * as XLSX from 'xlsx';
import { buildTemplateWorkbook, downloadWorkbook } from './spreadsheet-template.utils';
import RutUtils from './rut.utils';

// Helpers puros de "Facturación de Clínicas" (pawer-backend specs/125-clinic-billing-admin).

/**
 * The five financial states of a settlement, in the order the screen shows them.
 * Colors come from the approved design (badge background, text, dot and the
 * "strong" tone used for amounts and the history dots).
 */
export const STATUS_META = {
  not_invoiced: {
    label: 'Sin Facturar',
    bg: '#E7ECF4',
    fg: '#3B5170',
    dot: '#6B8CB3',
    strong: '#3B5170'
  },
  pending_payment: {
    label: 'Pendiente de Pago',
    bg: '#FFF4CC',
    fg: '#7A5A00',
    dot: '#E0A100',
    strong: '#9A6700'
  },
  credit_note_required: {
    label: 'Requiere NC',
    bg: '#FFE3D1',
    fg: '#9A3B00',
    dot: '#E0651A',
    strong: '#B3450A'
  },
  paid: { label: 'Pagado', bg: '#DFF5E8', fg: '#13693A', dot: '#1F9D55', strong: '#187A42' },
  canceled: { label: 'Anulado', bg: '#FDE6E6', fg: '#B42318', dot: '#E5484D', strong: '#C0262D' }
};

export const STATUS_KEYS = Object.keys(STATUS_META);

/** Status chips of the filter. "Anulado" is only seen with "Todos" (FR-004). */
export const FILTER_STATUSES = ['not_invoiced', 'pending_payment', 'credit_note_required', 'paid'];

/** States in which the invoice can be registered and attendances canceled. */
export const EDITABLE_STATUSES = ['not_invoiced', 'pending_payment', 'credit_note_required'];

/** Text shown under the "Cambiar estado" radios for the chosen state. */
export const STATUS_DESCRIPTIONS = {
  not_invoiced:
    'La liquidación vuelve a esperar la factura de la clínica: se quitan el folio, la fecha de emisión y los documentos (quedan en el historial).',
  pending_payment:
    'Factura registrada y lista para transferencia bancaria. Si había una NC pendiente, se da por registrada.',
  credit_note_required:
    'Se requiere Nota de Crédito por atenciones anuladas antes de continuar con el pago.',
  paid:
    'Pago ejecutado y cerrado exitosamente. Las atenciones vigentes quedan pagadas también en el portal de la clínica.',
  canceled: 'La factura queda anulada y no continúa en el flujo de pago.'
};

export const EVENT_LABELS = {
  invoice_registered: 'Factura Emitida',
  invoice_updated: 'Factura corregida',
  attendance_canceled: 'Atención anulada',
  attendance_restored: 'Atención restaurada',
  credit_note_requested: 'Nota de Crédito solicitada',
  credit_note_registered: 'Nota de Crédito registrada',
  credit_note_reopened: 'Nota de Crédito reabierta',
  paid: 'Pagado',
  payment_reverted: 'Pago revertido',
  canceled: 'Anulado',
  cancellation_reverted: 'Anulación revertida',
  reset_to_not_invoiced: 'Vuelta a Sin Facturar',
  receipt_uploaded: 'Comprobante subido'
};

// Eventos que la clínica genera desde Droopy (126).
export const CLINIC_EVENT_LABELS = {
  invoice_registered: 'Factura emitida por la clínica',
  credit_note_registered: 'Documento de ajuste subido por la clínica'
};

/**
 * Warnings about the PDFs the clinic uploaded (126), for Finance's review.
 * @param {{ pawerAmount: number, pdfReview?: object }} detail
 * @returns {string[]}
 */
export function pdfReviewWarnings(detail) {
  const review = (detail && detail.pdfReview) || {};
  const warnings = [];
  if (review.invoiceAmountMismatch) {
    warnings.push(
      `Monto de la factura: ${formatClp(
        review.invoiceDetectedAmount
      )} (no coincide con el Monto Pawer ${formatClp(detail.pawerAmount)}).`
    );
  }
  if (review.invoiceReceiverUnverified) {
    warnings.push(
      'No se pudo leer el RUT receptor de la factura: verifica que esté emitida a Pawer.'
    );
  }
  if (review.creditNoteAmountMismatch) {
    warnings.push(
      'El total del documento de ajuste no coincide con el monto de las atenciones anuladas.'
    );
  }
  return warnings;
}

// Color del punto del historial: el del estado al que lleva el evento.
const EVENT_STATUS = {
  invoice_registered: 'pending_payment',
  invoice_updated: 'pending_payment',
  attendance_canceled: 'canceled',
  attendance_restored: 'pending_payment',
  credit_note_requested: 'credit_note_required',
  credit_note_registered: 'pending_payment',
  credit_note_reopened: 'credit_note_required',
  paid: 'paid',
  payment_reverted: 'pending_payment',
  canceled: 'canceled',
  cancellation_reverted: 'pending_payment',
  reset_to_not_invoiced: 'not_invoiced',
  receipt_uploaded: 'paid'
};

/** Backend error codes (contract admin-clinic-settlements.md) → message for Finanzas. */
export const ERROR_MESSAGES = {
  CLINIC_SETTLEMENT_NOT_FOUND: 'La liquidación ya no existe.',
  INVALID_CUTOFF_DATE: 'La fecha de corte no es válida.',
  CUTOFF_NOT_CLOSED: 'El corte aún no cierra: todavía no se puede registrar la factura.',
  INVOICE_NUMBER_TAKEN: 'Otra liquidación de esta clínica ya tiene ese N° de factura.',
  INVALID_STATUS_TRANSITION:
    'Ese cambio no está permitido desde el estado actual de la liquidación.',
  INVALID_SETTLEMENT_DOCUMENT: 'El archivo no es válido o supera los 10 MB.',
  INVOICE_REQUIRED: 'Primero registra la factura de la clínica.',
  CANCELLATION_REASON_REQUIRED: 'Indica el motivo de la anulación (entre 3 y 255 caracteres).',
  INVOICE_NOT_FOUND: 'La atención no pertenece a esta liquidación.',
  SETTLEMENT_NOT_EDITABLE:
    'La liquidación está Pagada o Anulada: sus atenciones ya no se pueden modificar.',
  INVOICE_NOT_EDITABLE: 'La atención ya estaba anulada.',
  CREDIT_NOTE_REASON_REQUIRED: 'Indica el motivo de la Nota de Crédito (entre 3 y 255 caracteres).',
  INVALID_CREDIT_NOTE_AMOUNT: 'El monto de la Nota de Crédito debe ser un número entero mayor a 0.',
  ATTENDANCE_NOT_RESTORABLE:
    'Esta anulación ya no se puede deshacer: la factura o la Nota de Crédito ya se emitieron sin esta atención.',
  SETTLEMENT_DOCUMENT_NOT_FOUND: 'El archivo no está disponible.'
};

/**
 * Message for a failed service call: the known code, else the fallback.
 * @param {{code?: string}} res
 * @param {string} fallback
 */
export function settlementErrorMessage(res, fallback) {
  return ERROR_MESSAGES[res && res.code] || fallback;
}

/** `282000` → `$282.000`. */
export function formatClp(value) {
  const amount = Math.round(Number(value) || 0);
  const sign = amount < 0 ? '-' : '';
  return sign + '$' + String(Math.abs(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `2026-08-31` (date only, no time zone shift) or an ISO timestamp → `31/08/2026`.
 * Empty values give ''.
 */
export function formatDate(value) {
  if (!value) return '';
  const match = DATE_ONLY.exec(String(value));
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = moment(value);
  return date.isValid() ? date.format('DD/MM/YYYY') : '';
}

/** ISO timestamp → `15/09/2026 · 16:45 hrs` in the admin's local time. */
export function formatDateTime(value) {
  const date = moment(value);
  return value && date.isValid() ? date.format('DD/MM/YYYY · HH:mm [hrs]') : '';
}

/**
 * Second line of an attendance in the breakdown: date, vet who attended and
 * the member (tutor). The attendance id stays out of sight; search still finds it.
 * E.g. `15/09/2026 · Atendió Verónica Castro · Tutor: Camila R.`
 */
export function attendanceSubtitle(item) {
  return [
    formatDate(item.attendedAt || item.createdAt),
    item.vetName ? `Atendió ${item.vetName}` : '',
    item.userDisplayName ? `Tutor: ${item.userDisplayName}` : ''
  ]
    .filter(Boolean)
    .join(' · ');
}

/** `1 atención` / `4 atenciones`. */
export function attendancesLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? 'atención' : 'atenciones'}`;
}

/** `F° 004582`, or `Sin factura`. */
export function invoiceLabel(invoiceNumber) {
  return invoiceNumber ? `F° ${invoiceNumber}` : 'Sin factura';
}

/**
 * Note shown at the top of the detail, by status.
 * @param {object} detail - settlement detail from the API.
 * @returns {string}
 */
export function statusNote(detail) {
  if (!detail) return '';
  switch (detail.status) {
    case 'canceled':
      return detail.cancellationReason || 'Factura anulada por el equipo de Finanzas.';
    case 'credit_note_required':
      return creditNoteRequiredNote(detail);
    case 'pending_payment':
      return 'Factura registrada. Lista para transferencia bancaria.';
    case 'paid':
      return detail.paidAt
        ? `Pago ejecutado y cerrado exitosamente el ${formatDate(detail.paidAt)}.`
        : 'Pago ejecutado y cerrado exitosamente.';
    default:
      return detail.isCutoffClosed === false
        ? 'Corte en curso: las atenciones se siguen sumando hasta la fecha de corte.'
        : 'Atenciones acumuladas del periodo. Pendiente de emisión de factura por la clínica.';
  }
}

// "Requiere NC" by canceled attendances, by Finance's request (126) or both.
function creditNoteRequiredNote(detail) {
  const issued = `Factura emitida por ${formatClp(detail.pawerAmount)}.`;
  const request = detail.creditNoteRequest;
  const byCancellations = detail.creditNoteAmount
    ? ` Se requiere Nota de Crédito por ${formatClp(
        detail.creditNoteAmount
      )} debido a atenciones anuladas.`
    : '';
  if (!request) return issued + byCancellations;
  const amount = request.amount ? ` por ${formatClp(request.amount)}` : '';
  const lead = byCancellations
    ? ' Además, se solicitó una Nota de Crédito'
    : ' Se solicitó una Nota de Crédito';
  return `${issued}${byCancellations}${lead}${amount}: ${request.reason}.`;
}

/**
 * Whether choosing "Requiere NC" asks the clinic for a credit note by hand: only when no
 * attendance was canceled after the invoice (otherwise it reopens the credit note of
 * those cancellations, as before).
 * @param {{ attendanceItems?: object[] }} detail
 * @returns {boolean}
 */
export function isManualCreditNote(detail) {
  return !((detail && detail.attendanceItems) || []).some(item => item.canceledAfterInvoice);
}

/**
 * Suggested amount for a hand-requested credit note: what the clinic's invoice totals
 * above the Pawer amount, when the PDF total could be read.
 * @param {{ pawerAmount: number, pdfReview?: object }} detail
 * @returns {number|null}
 */
export function suggestedCreditNoteAmount(detail) {
  const invoiced = detail && detail.pdfReview && detail.pdfReview.invoiceDetectedAmount;
  if (!invoiced) return null;
  const difference = Math.round(invoiced - (detail.pawerAmount || 0));
  return difference > 0 ? difference : null;
}

/**
 * Reads a CLP amount typed by Finance ("156.243", "$156243").
 * @param {string|number} value
 * @returns {number|null} The integer amount, or null when empty or not a positive amount.
 */
export function parseClpInput(value) {
  const digits = String(value === null || value === undefined ? '' : value).replace(/\D/g, '');
  const amount = digits ? Number(digits) : 0;
  return amount > 0 ? amount : null;
}

/** Attendance items with the canceled ones last, keeping the API order otherwise. */
export function sortAttendanceItems(items) {
  return (items || [])
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const canceledDiff = (a.item.status === 'canceled') - (b.item.status === 'canceled');
      return canceledDiff || a.index - b.index;
    })
    .map(({ item }) => item);
}

function eventDetailParts(detail) {
  const parts = [];
  if (detail.invoiceNumber) parts.push(`F° ${detail.invoiceNumber}`);
  if (detail.previousInvoiceNumber) parts.push(`antes F° ${detail.previousInvoiceNumber}`);
  if (detail.invoiceId) parts.push(`Atención ${detail.invoiceId}`);
  if (detail.amount !== undefined && detail.amount !== null) parts.push(formatClp(detail.amount));
  if (detail.paidAt) parts.push(`Fecha de pago ${formatDate(detail.paidAt)}`);
  if (detail.reason) parts.push(`Motivo: ${detail.reason}`);
  if (detail.source === 'payments_file') parts.push('Desde Cargar Pagos');
  return parts;
}

/**
 * History entries for the timeline, newest first.
 * @param {Array<{type: string, detail: object, actorName: string, createdAt: string}>} events
 */
export function timelineEntries(events) {
  return [...(events || [])]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map(event => {
      const parts = eventDetailParts(event.detail || {});
      if (event.actorName) parts.push(`Por ${event.actorName}`);
      const status = EVENT_STATUS[event.type] || 'not_invoiced';
      const fromClinic = (event.detail || {}).source === 'clinic_portal';
      return {
        label:
          (fromClinic && CLINIC_EVENT_LABELS[event.type]) || EVENT_LABELS[event.type] || event.type,
        date: formatDateTime(event.createdAt),
        detail: parts.join(' · '),
        color: STATUS_META[status].strong
      };
    });
}

/** Saves a text file (e.g. the payroll CSV) through a temporary link. */
export function downloadTextFile(content, filename, type = 'text/csv;charset=utf-8;') {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Cargar Pagos ──────────────────────────────────────────────────────────

export const PAYMENTS_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const PAYMENTS_MAX_ROWS = 1000;

/** Required columns of the payments file, with the header text Finanzas sees. */
export const PAYMENT_COLUMNS = [
  { key: 'clinicRut', label: 'RUT clínica', aliases: ['rut clinica', 'rut de la clinica', 'rut'] },
  {
    key: 'invoiceNumber',
    label: 'N° de Factura',
    aliases: [
      'n de factura',
      'n factura',
      'no de factura',
      'nro de factura',
      'numero de factura',
      'folio'
    ]
  },
  { key: 'status', label: 'Estado', aliases: ['estado'] },
  { key: 'paymentDate', label: 'Fecha de pago', aliases: ['fecha de pago', 'fecha pago'] }
];

export const PAYMENTS_TEMPLATE_FILENAME = 'plantilla-cargar-pagos.xlsx';

/** The values the "Estado" column accepts; the template offers them as a dropdown. */
export const PAYMENT_STATUS_OPTIONS = ['Pagado', 'Nota de Crédito Subida'];

// Template columns, in PAYMENT_COLUMNS order, with the guidance shown on each header.
const PAYMENTS_TEMPLATE_COLUMNS = [
  { width: 18, note: 'Con o sin puntos y guion: 76.482.119-3 o 764821193.' },
  {
    width: 16,
    note: 'El folio de la factura (por ejemplo 64). Si viene con "F°" adelante, se ignora.'
  },
  { width: 26, list: PAYMENT_STATUS_OPTIONS },
  {
    width: 16,
    note:
      'DD/MM/AAAA, DD-MM-AAAA, DD.MM.AAAA o AAAA-MM-DD. Obligatoria para Pagado; vacía para Nota de Crédito Subida.'
  }
].map((column, index) => ({ header: PAYMENT_COLUMNS[index].label, ...column }));

// Example rows of the template's second sheet: one payment and one credit note, showing
// that the RUT goes with or without dots and dash.
const PAYMENTS_TEMPLATE_EXAMPLES = [
  ['76.482.119-3', '004582', 'Pagado', '15/10/2026'],
  ['764821193', '004583', 'Nota de Crédito Subida', '']
];

/**
 * Workbook of the "Cargar pagos" template: the first sheet ("Pagos") has only the
 * headers and is the one the upload reads; the second ("Ejemplo") shows filled rows.
 * "Estado" is a dropdown and every column is text.
 */
export function buildPaymentsTemplate() {
  return buildTemplateWorkbook({
    sheetName: 'Pagos',
    columns: PAYMENTS_TEMPLATE_COLUMNS,
    examples: PAYMENTS_TEMPLATE_EXAMPLES
  });
}

/** Saves the "Cargar pagos" template as an .xlsx file. */
export async function downloadPaymentsTemplate() {
  await downloadWorkbook(await buildPaymentsTemplate(), PAYMENTS_TEMPLATE_FILENAME);
}

/** Upper-case, no accents, single spaces: how the backend compares the "Estado" column. */
function normalizeStatusLabel(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Summary shown before applying a payments file, mirroring the backend's reading of
 * "Estado": payments grouped by date, credit notes, and rows the backend will reject
 * for an unknown status.
 * @param {Array<{rowNumber: number, invoiceNumber: string, status: string, paymentDate: string}>} rows
 */
export function summarizePaymentRows(rows) {
  const byDate = new Map();
  const creditNotes = [];
  const unknown = [];
  rows.forEach(row => {
    const status = normalizeStatusLabel(row.status);
    if (status === 'PAGADO') {
      const date = row.paymentDate || 'Sin fecha';
      if (!byDate.has(date)) byDate.set(date, []);
      byDate.get(date).push(row.invoiceNumber);
    } else if (status === 'NOTA DE CREDITO SUBIDA') {
      creditNotes.push(row.invoiceNumber);
    } else {
      unknown.push(row.rowNumber);
    }
  });
  return {
    payments: [...byDate].map(([date, invoices]) => ({ date, invoices })),
    paymentCount: [...byDate.values()].reduce((sum, invoices) => sum + invoices.length, 0),
    creditNotes,
    unknown
  };
}

/** "004582, 004583, … +3": at most five folios, then how many more. */
export function folioList(folios, max = 5) {
  const shown = folios.slice(0, max).join(', ');
  return folios.length > max ? `${shown} … +${folios.length - max}` : shown;
}

/**
 * Rejected rows grouped by reason, naming each row by its folio when known.
 * @param {Array<{rowNumber: number, reason: string}>} rejected
 * @param {Array<{rowNumber: number, invoiceNumber: string}>} rows - The rows that were sent.
 */
export function groupRejectedRows(rejected, rows) {
  const folioByRow = new Map(rows.map(row => [row.rowNumber, row.invoiceNumber]));
  const groups = new Map();
  rejected.forEach(({ rowNumber, reason }) => {
    if (!groups.has(reason)) groups.set(reason, []);
    const folio = folioByRow.get(rowNumber);
    groups.get(reason).push(folio ? `F° ${folio} (fila ${rowNumber})` : `Fila ${rowNumber}`);
  });
  return [...groups].map(([reason, items]) => ({ reason, items }));
}

// "N° de Factura" → "n de factura": sin mayúsculas, tildes ni símbolos (°, º, .).
function normalizeHeader(cell) {
  return String(cellValue(cell) ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Una celda puede venir como objeto de SheetJS ({ t, v, w }) o como valor plano.
function isSheetCell(cell) {
  return cell !== null && typeof cell === 'object' && !(cell instanceof Date) && 't' in cell;
}

function cellValue(cell) {
  return isSheetCell(cell) ? cell.v : cell;
}

function cellText(cell) {
  if (cell === null || cell === undefined) return '';
  // `w` es el texto formateado: conserva los ceros a la izquierda de un folio numérico.
  const text = isSheetCell(cell) ? cell.w ?? cell.v : cell;
  return String(text ?? '').trim();
}

function pad(n) {
  return String(n).padStart(2, '0');
}

/**
 * A payment date cell → `DD/MM/AAAA`. Excel stores dates as serial numbers (or
 * Date objects); text is kept as typed (an ISO `YYYY-MM-DD` is turned around) and
 * the backend rejects anything that is not a valid date.
 */
export function normalizePaymentDate(cell) {
  const value =
    isSheetCell(cell) && cell.t !== 'n' && cell.t !== 'd' ? cellText(cell) : cellValue(cell);
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? ''
      : `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()}`;
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed ? `${pad(parsed.d)}/${pad(parsed.m)}/${parsed.y}` : String(value);
  }
  const text = String(value ?? '').trim();
  const iso = DATE_ONLY.exec(text);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  // DD/MM/AAAA, DD-MM-AAAA o DD.MM.AAAA (como en la carga de reembolsos) → DD/MM/AAAA.
  const dayFirst = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(text);
  return dayFirst ? `${pad(Number(dayFirst[1]))}/${pad(Number(dayFirst[2]))}/${dayFirst[3]}` : text;
}

/**
 * The folio as registered: without the "F°", "N°" or "Folio" prefix the admin's tables
 * show, which travels along when the cell is copied from there.
 */
export function normalizeInvoiceNumber(text) {
  return String(text ?? '')
    .trim()
    .replace(/^(?:folio\s*:?|[fn]\s*[°º˚]|nro\.?|no\.)\s*/i, '')
    .trim();
}

function detectPaymentColumns(headerRow) {
  const headers = (headerRow || []).map(normalizeHeader);
  const columns = {};
  PAYMENT_COLUMNS.forEach(({ key, aliases }) => {
    const index = headers.findIndex(header => aliases.includes(header));
    if (index !== -1) columns[key] = index;
  });
  return columns;
}

function isBlankRow(row) {
  return (row || []).every(cell => cellText(cell) === '');
}

/**
 * Turns the first sheet (header row + data rows) into the rows of
 * `POST /clinic-settlements/payments`. `rowNumber` is the spreadsheet row, so the
 * rejected rows of the summary can be found in the file (header = row 1).
 * @param {Array<Array<*>>} matrix - rows of cells (plain values or SheetJS cells).
 * @returns {{ rows: Array<{rowNumber: number, clinicRut: string, invoiceNumber: string, status: string, paymentDate: string}>, missingColumns: string[] }}
 */
export function parsePaymentsMatrix(matrix) {
  const lines = matrix || [];
  // Los encabezados son la primera fila con contenido; matrix[0] es la fila 1 de la planilla.
  const headerIndex = lines.findIndex(row => !isBlankRow(row));
  const columns = detectPaymentColumns(lines[headerIndex]);
  const missingColumns = PAYMENT_COLUMNS.filter(({ key }) => columns[key] === undefined).map(
    ({ label }) => label
  );
  if (missingColumns.length) return { rows: [], missingColumns };

  const rows = [];
  lines.forEach((row, index) => {
    if (index <= headerIndex || isBlankRow(row)) return;
    rows.push({
      rowNumber: index + 1,
      // Con o sin puntos y guion: se envía normalizado (dígitos + verificador).
      clinicRut: RutUtils.clean(cellText(row[columns.clinicRut])),
      invoiceNumber: normalizeInvoiceNumber(cellText(row[columns.invoiceNumber])),
      status: cellText(row[columns.status]),
      paymentDate: normalizePaymentDate(row[columns.paymentDate])
    });
  });
  return { rows, missingColumns };
}

// Matriz de celdas de SheetJS (no sus valores) para conservar tipo y texto formateado.
// Empieza en A1 para que el índice de la fila sea su número en la planilla menos 1.
function sheetToCellMatrix(sheet) {
  if (!sheet || !sheet['!ref']) return [];
  const range = XLSX.utils.decode_range(sheet['!ref']);
  const matrix = [];
  for (let r = 0; r <= range.e.r; r++) {
    const row = [];
    for (let c = 0; c <= range.e.c; c++) {
      row.push(sheet[XLSX.utils.encode_cell({ r, c })] || null);
    }
    matrix.push(row);
  }
  return matrix;
}

/**
 * Reads a payments .xlsx or .csv (first sheet). A .csv is read as text so accents
 * survive and every value stays as typed.
 * @param {File} file
 */
export async function readPaymentsFile(file) {
  const workbook = /\.csv$/i.test(file.name)
    ? XLSX.read(await file.text(), { type: 'string', raw: true })
    : XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
  return parsePaymentsMatrix(sheetToCellMatrix(workbook.Sheets[workbook.SheetNames[0]]));
}
